import { useEffect, useCallback, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { type Root } from 'react-dom/client';
import { UnitHoverCard } from '../UnitHoverCard';
import { ClusterPopup } from '../ClusterPopup';
import { SmallClusterPopup } from '../SmallClusterPopup';
import type { Unit, IndustryType } from '@/types';
import { POPUP_THRESHOLDS, HOVER_CONFIG, POPUP_CONFIG, BOUNDS_PADDING, ZOOM_THRESHOLDS } from '../mapConfig';

/**
 * Map Popups Lifecycle Management Hook
 *
 * Manages popup rendering with intelligent routing:
 * - Individual units: UnitHoverCard
 * - Small clusters (6-10): SmallClusterPopup (icon grid)
 * - Large clusters (11+): ClusterPopup (company-grouped list)
 *
 * Performance: Smart positioning, React portal rendering, cleanup on unmount
 */

interface UseMapPopupsProps {
  map: mapboxgl.Map | null;
  mapLoaded: boolean;
  popup: mapboxgl.Popup | null;
  popupRoot: Root | null;
  popupNode: HTMLDivElement | null;
  hoveredUnit: Unit | null;
  hoveredCluster: { units: Unit[]; coordinates: [number, number] } | null;
  selectedUnit: Unit | null;
  allUnits?: Unit[]; // All units for zoom calculation
  isMobile: boolean;
  onUnitClick?: (unit: Unit | null) => void;
  onUnitHover?: (unit: Unit | null) => void;
  onClusterHover?: (data: { units: Unit[]; coordinates: [number, number] } | null) => void;
  hoverLeaveTimeoutRef?: React.MutableRefObject<NodeJS.Timeout | null>; // Shared timeout ref from useMapEvents
  /** Navigation function - required for UnitHoverCard which renders in popup portals outside Router context */
  navigate: (path: string) => void;
  filterActions?: {
    setSelectedCompanyIds: (ids: string[]) => void;
    setSelectedGroupIds: (ids: string[]) => void;
    setSelectedIndustryTypes: (types: IndustryType[]) => void;
    setSelectedModels: (models: string[]) => void;
    saveFiltersForRestore: () => void;
    restoreSavedFilters: () => void;
    setShouldExpandAll: (value: boolean) => void;
  };
  hasSavedFilters?: boolean;
  isClusterSticky?: boolean; // New prop to prevent auto-close on mouseleave
  getShareableUrl: (extraParams?: Record<string, string>) => string;
}

interface PopupAnchor {
  anchor: 'top' | 'bottom' | 'left' | 'right';
  offset: [number, number];
}

export function useMapPopups({
  map,
  mapLoaded,
  popup,
  popupRoot,
  popupNode,
  hoveredUnit,
  hoveredCluster,
  selectedUnit,
  allUnits,
  isMobile,
  onUnitClick,
  onUnitHover,
  onClusterHover,
  hoverLeaveTimeoutRef, // Shared timeout ref from useMapEvents
  navigate,
  filterActions,
  hasSavedFilters = false,
  isClusterSticky = false,
  getShareableUrl,
}: UseMapPopupsProps) {
  // Track pending timeout refs to prevent memory leaks and race conditions
  const popupLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clusterLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const unitInClusterPopupRef = useRef<mapboxgl.Popup | null>(null);
  const unitInClusterRootRef = useRef<Root | null>(null);
  const activePopupRef = useRef<mapboxgl.Popup | null>(null); // Track current active popup for proper cleanup
  const activePopupTypeRef = useRef<'small-cluster' | 'large-cluster' | 'unit-hover' | null>(null);
  const activePopupCoordinatesRef = useRef<string | null>(null); // Store stringified coordinates to detect position changes
  const activeUnitIdRef = useRef<string | null>(null); // Track current unit ID to prevent unnecessary re-renders
  const isNavigatingRef = useRef(false); // Guard to prevent mouseleave from clearing state during navigation

  // Cleanup all timeouts and popups on unmount
  useEffect(() => {
    return () => {
      if (popupLeaveTimeoutRef.current) {
        clearTimeout(popupLeaveTimeoutRef.current);
      }
      if (clusterLeaveTimeoutRef.current) {
        clearTimeout(clusterLeaveTimeoutRef.current);
      }
      if (activePopupRef.current) {
        activePopupRef.current.remove();
      }
      if (unitInClusterPopupRef.current) {
        unitInClusterPopupRef.current.remove();
      }
      if (unitInClusterRootRef.current) {
        unitInClusterRootRef.current.unmount();
      }
    };
  }, []);
  // Calculate smart popup positioning based on marker location
  // Mobile: Force top/bottom only with center alignment
  // Desktop: Use quadrant logic with sidebar awareness
  const calculatePopupAnchor = useCallback((coordinates: [number, number]): PopupAnchor => {
    if (!map) return { anchor: 'bottom', offset: [0, -20] };

    const markerPos = map.project(coordinates);
    const mapCanvas = map.getCanvas();
    const canvasRect = mapCanvas.getBoundingClientRect();

    // Adjust offset based on zoom level - smaller dots need closer tooltips
    const zoom = map.getZoom();
    const offsetDistance = zoom <= 4 ? 12 : 20;

    if (isMobile) {
      // MOBILE: Force top/bottom only, center horizontally
      // Simpler logic for narrow viewports, prevents left/right overflow

      // Use 40% threshold (more aggressive top placement to avoid keyboards)
      if (markerPos.y < canvasRect.height * POPUP_CONFIG.mobile.anchorThreshold) {
        return {
          anchor: 'top',
          offset: [0, offsetDistance]
        };
      } else {
        return {
          anchor: 'bottom',
          offset: [0, -offsetDistance]
        };
      }
    } else {
      // DESKTOP: Use effective viewport logic with sidebar awareness
      let anchor: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
      let offset: [number, number] = [0, -offsetDistance];

      const SIDEBAR_WIDTH = 360;
      const thresholds = POPUP_CONFIG.desktop.anchorThresholds;

      // Vertical positioning (unchanged)
      if (markerPos.y < canvasRect.height * thresholds.top) {
        anchor = 'top';
        offset = [0, offsetDistance];
      } else if (markerPos.y > canvasRect.height * thresholds.bottom) {
        anchor = 'bottom';
        offset = [0, -offsetDistance];
      } else {
        // Middle vertical area - use horizontal positioning
        // Calculate position relative to EFFECTIVE viewport (excluding sidebar)
        const effectiveWidth = canvasRect.width - SIDEBAR_WIDTH;
        const effectiveX = (markerPos.x - SIDEBAR_WIDTH) / effectiveWidth;

        // Use symmetric 25%/75% thresholds for true edge detection
        // CORRECTED LOGIC: Push tooltips away from edges to stay visible
        if (effectiveX < 0.25) {
          // Far left edge of effective viewport → push popup RIGHT (away from edge)
          anchor = 'left';  // Popup appears to the RIGHT of marker
          offset = [offsetDistance, 0];
        } else if (effectiveX > 0.75) {
          // Far right edge of effective viewport → push popup LEFT (away from edge)
          anchor = 'right';  // Popup appears to the LEFT of marker
          offset = [-offsetDistance, 0];
        }
        // else: middle area (25%-75%), keep bottom anchor
      }

      return { anchor, offset };
    }
  }, [map, isMobile]);

  // Pan map to ensure popup is fully visible without obstruction
  // Accounts for sidebar on desktop and safe areas on mobile
  const ensurePopupVisible = useCallback((popup: mapboxgl.Popup, _coordinates: [number, number]) => {
    if (!map) return;

    // Wait for popup to be rendered in DOM
    requestAnimationFrame(() => {
      const popupElement = popup.getElement();
      if (!popupElement) return;

      const popupRect = popupElement.getBoundingClientRect();
      const mapCanvas = map.getCanvas();
      const mapRect = mapCanvas.getBoundingClientRect();

      // Get safe areas based on mobile vs desktop
      const safeAreas = isMobile ? POPUP_CONFIG.mobile.safeAreas : POPUP_CONFIG.desktop.safeAreas;

      // Calculate effective viewport bounds (compensating for sidebar and safe areas)
      const effectiveLeft = mapRect.left + safeAreas.left;
      const effectiveRight = mapRect.right - safeAreas.right;
      const effectiveTop = mapRect.top + safeAreas.top;
      const effectiveBottom = mapRect.bottom - safeAreas.bottom;

      // Calculate required pan to keep popup fully visible
      let panX = 0;
      let panY = 0;

      // Check horizontal bounds
      if (popupRect.left < effectiveLeft) {
        panX = popupRect.left - effectiveLeft - 20; // Additional 20px padding
      } else if (popupRect.right > effectiveRight) {
        panX = popupRect.right - effectiveRight + 20; // Additional 20px padding
      }

      // Check vertical bounds
      if (popupRect.top < effectiveTop) {
        panY = popupRect.top - effectiveTop - 20; // Additional 20px padding
      } else if (popupRect.bottom > effectiveBottom) {
        panY = popupRect.bottom - effectiveBottom + 20; // Additional 20px padding
      }

      // If popup is obstructed, pan the map
      if (panX !== 0 || panY !== 0) {
        const center = map.getCenter();
        const point = map.project(center);
        const newPoint = new mapboxgl.Point(point.x + panX, point.y + panY);
        const newCenter = map.unproject(newPoint);

        map.easeTo({
          center: newCenter,
          duration: 300,
          easing: (t) => t * (2 - t), // easeOutQuad for smooth deceleration
        });
      }
    });
  }, [map, isMobile]);

  // Determine popup type based on cluster size
  const popupType = useMemo(() => {
    if (hoveredCluster) {
      const clusterSize = hoveredCluster.units.length;
      if (clusterSize <= POPUP_THRESHOLDS.SMALL_CLUSTER_MAX) {
        return 'small-cluster';
      } else {
        return 'large-cluster';
      }
    }
    if (hoveredUnit && !selectedUnit) {
      // No popup at zoom 1-6 (dots too small for hover interaction)
      if (map && map.getZoom() <= ZOOM_THRESHOLDS.NO_HOVER_THRESHOLD) {
        return null;
      }
      return 'unit-hover';
    }
    return null;
  }, [hoveredCluster, hoveredUnit, selectedUnit, map]);

  // Clear all pending popup leave timeouts immediately when hover state is explicitly cleared
  // This prevents race conditions where a timeout fires after clicking away
  useEffect(() => {
    if (!hoveredUnit && !hoveredCluster) {
      // Clear any pending timeouts when hover is explicitly cleared (e.g., from background click)
      if (popupLeaveTimeoutRef.current) {
        clearTimeout(popupLeaveTimeoutRef.current);
        popupLeaveTimeoutRef.current = null;
      }
      if (clusterLeaveTimeoutRef.current) {
        clearTimeout(clusterLeaveTimeoutRef.current);
        clusterLeaveTimeoutRef.current = null;
      }
    }
  }, [hoveredUnit, hoveredCluster]);

  // Define onClose callback to close active popup
  const handleClosePopup = useCallback(() => {
    if (activePopupRef.current) {
      activePopupRef.current.remove();
      activePopupRef.current = null;
      activePopupTypeRef.current = null;
      activePopupCoordinatesRef.current = null;
    }
    // Also notify parent to clear hover state if needed
    if (onClusterHover) {
        onClusterHover(null);
    }
    if (onUnitHover) {
        onUnitHover(null);
    }
  }, [onClusterHover, onUnitHover]);

  // Render popup based on type
  useEffect(() => {
    if (!map || !mapLoaded || !popup || !popupRoot || !popupNode) return;

    // MOBILE: Disable desktop popups entirely (drawer system handles all mobile info display)
    if (isMobile) {
      if (activePopupRef.current) {
        activePopupRef.current.remove();
        activePopupRef.current = null;
        activePopupTypeRef.current = null;
      }
      return;
    }

    // PRIORITY: Cluster popups take precedence over individual unit hover
    // This prevents unit hover cards from showing when hovering icons inside a cluster popup
    if (hoveredCluster) {
      // CLUSTER POPUP: Show SmallClusterPopup or ClusterPopup
      const coordinates = hoveredCluster.coordinates;
      const location = hoveredCluster.units[0]?.location?.address?.split(',')[0] || 'Location';
      
      // Determine if we can reuse existing popup
      const coordsKey = `${coordinates[0]},${coordinates[1]}`;
      const canReusePopup = activePopupRef.current && 
                            activePopupTypeRef.current === popupType &&
                            activePopupCoordinatesRef.current === coordsKey;

      // Render React content (always update content)
      // Keys ensure stable React reconciliation for portal-rendered components
      if (popupType === 'small-cluster') {
        // Small cluster (2-10 units): SmallClusterPopup with icon grid
        popupRoot.render(
          <SmallClusterPopup
            key={`sm-cluster-${coordsKey}-${hoveredCluster.units.length}`}
            units={hoveredCluster.units}
            location={location}
            onUnitHover={(unit: Unit | null) => onUnitHover?.(unit)}
            onUnitClick={(unit: Unit) => onUnitClick?.(unit)}
            isMobile={isMobile}
            initialActiveUnit={hoveredUnit} // Pass sidebar hovered unit to set as initial active
            navigate={(path) => {
              // Set navigation guard to prevent mouseleave from clearing state
              isNavigatingRef.current = true;
              setTimeout(() => { isNavigatingRef.current = false; }, 100);
              navigate?.(path);
            }}
            onClose={handleClosePopup}
            getShareableUrl={() => getShareableUrl({ cluster: `${coordinates[0].toFixed(6)},${coordinates[1].toFixed(6)}` })}
          />
        );
      } else {
        // Large cluster (11+ units): ClusterPopup with company grouping
        popupRoot.render(
          <ClusterPopup
            key={`lg-cluster-${coordsKey}-${hoveredCluster.units.length}`}
            units={hoveredCluster.units}
            allUnits={allUnits}
            location={location}
            onViewAll={() => {
              if (map && hoveredCluster.units.length > 0) {
                // Calculate bounds from all units in the cluster
                const bounds = new mapboxgl.LngLatBounds();
                hoveredCluster.units.forEach((unit) => {
                  if (unit.location) {
                    bounds.extend([unit.location.lng, unit.location.lat]);
                  }
                });

                // Close the popup before zooming
                handleClosePopup();

                // Zoom to fit all units with appropriate padding
                const paddingOptions = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;
                map.fitBounds(bounds, {
                  padding: paddingOptions,
                  duration: 800,
                  maxZoom: 15, // Higher zoom for cluster detail
                  essential: true,
                });
              }
            }}
            isMobile={isMobile}
            map={map}
            filterActions={filterActions || {
                setSelectedCompanyIds: () => {},
                setSelectedGroupIds: () => {},
                setSelectedIndustryTypes: () => {},
                setSelectedModels: () => {},
                saveFiltersForRestore: () => {},
                restoreSavedFilters: () => {},
                setShouldExpandAll: () => {},
            }}
            hasSavedFilters={hasSavedFilters}
            onClose={handleClosePopup}
            getShareableUrl={() => getShareableUrl({ cluster: `${coordinates[0].toFixed(6)},${coordinates[1].toFixed(6)}` })}
          />
        );
      }

      // Only recreate Mapbox popup if position or type changed
      if (!canReusePopup) {
        const { anchor, offset } = calculatePopupAnchor(coordinates);

        // Remove old popup first if it exists
        if (activePopupRef.current) {
          activePopupRef.current.remove();
        }

        // Mobile-specific popup configuration for centering
        const mobileAnchor: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
        const mobileOffset: [number, number] = [0, -20];

        const newPopup = new mapboxgl.Popup({
          anchor: isMobile ? mobileAnchor : anchor,
          offset: isMobile ? mobileOffset : offset,
          closeButton: false,
          closeOnClick: false,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 'none',
          className: isMobile ? 'mobile-cluster-popup' : 'cluster-popup',
        });

        newPopup
          .setLngLat(coordinates)
          .setDOMContent(popupNode)
          .addTo(map);
        
        // Ensure popup is fully visible
        ensurePopupVisible(newPopup, coordinates);
        
        // Store reference for proper cleanup
        activePopupRef.current = newPopup;
        activePopupTypeRef.current = popupType as 'small-cluster' | 'large-cluster';
        activePopupCoordinatesRef.current = coordsKey;

        // Apply z-index to cluster popup container
        const clusterPopupElement = newPopup.getElement();
        if (clusterPopupElement) {
          clusterPopupElement.style.zIndex = '1000';

          // Only attach hover handlers on desktop
          if (!isMobile) {
            // Shared function to clear all leave timeouts - keeps popup alive
            const clearLeaveTimeouts = () => {
              if (clusterLeaveTimeoutRef.current) {
                clearTimeout(clusterLeaveTimeoutRef.current);
                clusterLeaveTimeoutRef.current = null;
              }
              // Also clear the marker's hover timeout so popup stays open
              if (hoverLeaveTimeoutRef?.current) {
                clearTimeout(hoverLeaveTimeoutRef.current);
                hoverLeaveTimeoutRef.current = null;
              }
            };

            clusterPopupElement.addEventListener('mouseenter', clearLeaveTimeouts);

            // Keep popup alive while cursor is moving within it
            // This prevents false dismissals from internal element transitions
            clusterPopupElement.addEventListener('mousemove', clearLeaveTimeouts);

            if (!isClusterSticky) {
              clusterPopupElement.addEventListener('mouseleave', (e: MouseEvent) => {
                // Skip if we're navigating to detail view - prevents race condition
                // where navigation triggers mouseleave before URL sync completes
                if (isNavigatingRef.current) {
                  return;
                }

                // Check if cursor moved to a child element - don't dismiss
                const relatedTarget = e.relatedTarget as Node | null;
                if (relatedTarget && clusterPopupElement.contains(relatedTarget)) {
                  return;
                }

                // Set timeout to allow mouse travel back to marker
                clusterLeaveTimeoutRef.current = setTimeout(() => {
                  if (onClusterHover) {
                    onClusterHover(null);
                  }
                  // Also clear unit hover to prevent individual popup from showing
                  if (onUnitHover) {
                    onUnitHover(null);
                  }
                  clusterLeaveTimeoutRef.current = null;
                }, HOVER_CONFIG.popupLeaveDelay);
              });
            }
          }
        }
      }
    } else if (hoveredUnit && !selectedUnit && !hoveredCluster) {
      // INDIVIDUAL TRUCK POPUP: Show UnitHoverCard
      // Only show when NOT hovering a cluster (to prevent interference)
      const coordinates: [number, number] = [
        hoveredUnit.location.lng,
        hoveredUnit.location.lat,
      ];

      // Determine if we can reuse existing popup
      const coordsKey = `${coordinates[0]},${coordinates[1]}`;
      const canReusePopup = activePopupRef.current &&
                            activePopupTypeRef.current === 'unit-hover' &&
                            activePopupCoordinatesRef.current === coordsKey;

      // Skip re-render if same unit is already displayed (prevents image flicker).
      // Check unit ID match even without canReuse - the popup DOM may still be valid
      // even if a brief null state transition cleared the coordinate refs.
      const isSameUnit = activeUnitIdRef.current === hoveredUnit.id;
      if (isSameUnit && canReusePopup) {
        return;
      }
      // If same unit but popup needs repositioning, only update content (not DOM)
      if (isSameUnit && activePopupRef.current && activePopupTypeRef.current === 'unit-hover') {
        // Same unit, popup still exists — just update coordinates ref and reposition
        activePopupRef.current.setLngLat(coordinates);
        activePopupCoordinatesRef.current = coordsKey;
        return;
      }
      activeUnitIdRef.current = hoveredUnit.id;

      // Render React content with stable key for portal reconciliation
      popupRoot.render(
        <UnitHoverCard
          key={`unit-hover-${hoveredUnit.id}`}
          unit={hoveredUnit}
          onClick={() => onUnitClick?.(hoveredUnit)}
          onNavigate={navigate}
          isMobile={isMobile}
          getShareableUrl={() => getShareableUrl({ unit: hoveredUnit.unit_code.toLowerCase(), view: 'detail' })}
        />
      );

      // Only recreate Mapbox popup if needed
      if (!canReusePopup) {
        const { anchor, offset } = calculatePopupAnchor(coordinates);

        // Remove old popup first
        if (activePopupRef.current) {
          activePopupRef.current.remove();
        }

        // Mobile-specific popup configuration for centering
        const mobileAnchor: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
        const mobileOffset: [number, number] = [0, -20];

        const newPopup = new mapboxgl.Popup({
          anchor: isMobile ? mobileAnchor : anchor,
          offset: isMobile ? mobileOffset : offset,
          closeButton: false,
          closeOnClick: false,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 'none',
          className: isMobile ? 'mobile-unit-popup' : 'unit-hover-popup',
        });

        newPopup
          .setLngLat(coordinates)
          .setDOMContent(popupNode)
          .addTo(map);
        
        // Ensure popup is fully visible
        ensurePopupVisible(newPopup, coordinates);
        
        // Store reference for proper cleanup
        activePopupRef.current = newPopup;
        activePopupTypeRef.current = 'unit-hover';
        activePopupCoordinatesRef.current = coordsKey;

        // Apply z-index to unit popup
        const unitPopupElement = newPopup.getElement();
        if (unitPopupElement) {
          unitPopupElement.style.zIndex = '1100'; // Higher than cluster popup

          // Only attach hover handlers on desktop
          if (!isMobile) {
            // Clear any pending leave timeout when entering or moving within popup
            const clearLeaveTimeouts = () => {
              if (popupLeaveTimeoutRef.current) {
                clearTimeout(popupLeaveTimeoutRef.current);
                popupLeaveTimeoutRef.current = null;
              }
              // CRITICAL FIX: Clear the marker's hover timeout so popup stays open
              if (hoverLeaveTimeoutRef?.current) {
                clearTimeout(hoverLeaveTimeoutRef.current);
                hoverLeaveTimeoutRef.current = null;
              }
            };

            unitPopupElement.addEventListener('mouseenter', () => {
              clearLeaveTimeouts();
            });

            // Keep popup alive while cursor is moving within it
            // This prevents false dismissals from internal element transitions
            unitPopupElement.addEventListener('mousemove', clearLeaveTimeouts);

            unitPopupElement.addEventListener('mouseleave', (e: MouseEvent) => {
              // Check if we're actually leaving the popup (not just moving to a child element)
              const relatedTarget = e.relatedTarget as Node | null;
              if (relatedTarget && unitPopupElement.contains(relatedTarget)) {
                // Cursor moved to a child element - don't dismiss
                return;
              }

              // Set timeout to allow mouse travel back to marker
              popupLeaveTimeoutRef.current = setTimeout(() => {
                if (onUnitHover) {
                  onUnitHover(null);
                }
                popupLeaveTimeoutRef.current = null;
              }, HOVER_CONFIG.popupLeaveDelay);
            });
          }
        }
      }
    } else {
      // No hover - hide all popups (only if there's something to clean up)
      if (!activePopupRef.current && !unitInClusterPopupRef.current) {
        // Nothing to clean up - skip expensive DOM operations
        return;
      }

      if (activePopupRef.current) {
        activePopupRef.current.remove();
        activePopupRef.current = null;
        activePopupTypeRef.current = null;
        activePopupCoordinatesRef.current = null;
        activeUnitIdRef.current = null;
      }

      // Also clean up unit-in-cluster popup if it exists
      if (unitInClusterPopupRef.current) {
        unitInClusterPopupRef.current.remove();
        unitInClusterPopupRef.current = null;
      }
      if (unitInClusterRootRef.current) {
        unitInClusterRootRef.current.unmount();
        unitInClusterRootRef.current = null;
      }
    }

    // Cleanup on effect re-run is minimal as we manage references
  }, [
    map,
    mapLoaded,
    popup,
    popupRoot,
    popupNode,
    hoveredUnit,
    hoveredCluster,
    selectedUnit,
    isMobile,
    popupType,
    calculatePopupAnchor,
    onUnitClick,
    onUnitHover,
    onClusterHover,
    handleClosePopup,
    navigate,
    filterActions,
    isClusterSticky,
  ]);
}
